/**
 * Section 02 — Core Concepts.
 *
 * The mental model. Reading this section is what separates "I clicked
 * buttons and somehow it worked" from "I know what's happening end to
 * end and can debug it when something goes sideways". Covers: signal
 * flow, the alert object, position lifecycle, broker model, money flow,
 * the risk gate, and the difference between auto-trade vs manual.
 */
import React from 'react';
import { Box, Typography, List, ListItem, ListItemText, Table, TableHead, TableRow, TableCell, TableBody, Paper } from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import GuideCallout from '../GuideCallout';
import GuideExample from '../GuideExample';
import GuideCodeBlock from '../GuideCodeBlock';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography
    id={`anchor-${id}`}
    variant="h5"
    sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}
  >
    {children}
  </Typography>
);

const H3 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2.5, mb: 0.75 }}>{children}</Typography>
);

const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>
    {children}
  </Typography>
);

const Section_concepts: GuideSection = {
  id: 'concepts',
  label: 'Core Concepts',
  group: 'getting-started',
  icon: <SchoolIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Core Concepts</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        The mental model. Once these are clear, every other section of the guide reads as a refinement —
        which knob to turn for which behaviour.
      </Typography>

      <H id="signal-flow">Signal flow — from idea to position</H>
      <P>
        Every trade earngush executes follows the same pipeline. The diagram below uses a left-to-right
        flow; each box is a distinct concern with its own settings and logs.
      </P>

      <Paper variant="outlined" sx={{ p: 2, my: 2, overflowX: 'auto' }}>
        <Box component="pre" sx={{ m: 0, fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem', lineHeight: 1.5 }}>{`
  ┌──────────────┐    ┌────────────┐    ┌──────────┐    ┌──────────────┐    ┌──────────┐
  │ TradingView  │───▶│            │    │          │    │              │    │          │
  │   (Pine)     │    │            │    │          │    │              │    │          │
  └──────────────┘    │            │    │          │    │              │    │          │
                      │  Webhook   │───▶│  Alert   │───▶│  Order Gate  │───▶│  Broker  │
  ┌──────────────┐    │  (auth +   │    │  (dedup, │    │ (kill switch │    │ (Delta/  │
  │  Sniper /    │───▶│  parse)    │    │   park,  │    │  + daily     │    │ Zerodha) │
  │  RockBand /  │    │            │    │   ack)   │    │  loss cap)   │    │          │
  │  DailyBlast  │    │            │    │          │    │              │    │          │
  └──────────────┘    └────────────┘    └──────────┘    └──────────────┘    └──────────┘
        signal source       ingress           triage          gate              fill
                                                                                  │
                                                                                  ▼
                                                                          ┌──────────────┐
                                                                          │  Position    │
                                                                          │  (Trade row, │
                                                                          │   SL + TSL,  │
                                                                          │   target)    │
                                                                          └──────────────┘
`}</Box>
      </Paper>

      <P>
        Read left to right. A signal originates either from <strong>your own Pine script on TradingView</strong>{' '}
        (which fires a webhook to earngush), or from the in-house <strong>RockBand scanner</strong> that
        evaluates candle data on a schedule. (The Sniper and DailyBlast engines are legacy — the polling /
        scheduled-scan automation now runs RockBand only.) Both paths converge on the same alert object — the
        only difference is the <code>signalSource</code> field that records where the alert came from.
      </P>
      <P>
        After ingress, the alert is triaged (deduped against recent same-symbol alerts, parked if the symbol
        has no active broker credentials, acknowledged if it's a new actionable signal). If you've enabled
        Auto Trade, an <em>entry</em> then passes the gating ladder (per-symbol direction → no-stacking →
        portfolio reserve) and the order gate (kill switch + daily-loss cap) before reaching the broker.
        Protective <em>exits</em> skip the gate entirely so positions can always be closed.
      </P>
      <P>
        Once filled, the alert becomes a <strong>Trade</strong> — a long-lived row with status, P&L, an SL
        order, an optional Target order, and a TSL state machine that follows price.
      </P>

      <H id="alert-anatomy">The Alert object — what's in it</H>
      <P>
        Every webhook hit (TradingView or in-house) produces one Alert row. The fields you'll see in the
        Recent Alerts panel + Logs:
      </P>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>What it is</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell><code>ticker</code></TableCell><TableCell>Symbol the signal applies to (e.g. <code>BTCUSD</code>, <code>NIFTY</code>).</TableCell></TableRow>
          <TableRow><TableCell><code>action</code></TableCell><TableCell>One of BUY / SHORT / SELL / COVER / SELL_FLAT / COVER_FLAT. See Glossary for the differences.</TableCell></TableRow>
          <TableRow><TableCell><code>entryLevel</code></TableCell><TableCell>Suggested entry price. May be 0 if the script wants market-fill.</TableCell></TableRow>
          <TableRow><TableCell><code>isl</code></TableCell><TableCell>Initial Stop Loss price. The platform plants this as a real stop-order at the broker once the entry is filled.</TableCell></TableRow>
          <TableRow><TableCell><code>target</code></TableCell><TableCell>Optional take-profit price. Same — plants a real limit-order at the broker.</TableCell></TableRow>
          <TableRow><TableCell><code>strength</code></TableCell><TableCell>HIGH / MEDIUM / LOW. Drives whether speech-alerts / Telegram fire, and is shown as a colour chip.</TableCell></TableRow>
          <TableRow><TableCell><code>confirmed</code></TableCell><TableCell>Y or N. Auto-trade only fires on Y; N alerts always wait for manual review.</TableCell></TableRow>
          <TableRow><TableCell><code>signalSource</code></TableCell><TableCell>The originator — e.g. <code>JSR_CLR</code> (TradingView pipe-format), <code>SNIPER</code>, <code>ROCKBAND</code>, <code>DAILYBLAST</code>.</TableCell></TableRow>
        </TableBody>
      </Table>

      <GuideCallout kind="info" title="Why CONFIRMED matters">
        Pine scripts often "preview" a signal mid-bar (e.g. as soon as price crosses a level), then the bar
        closes confirming or refuting it. <code>CONFIRMED=N</code> tags the preview; <code>CONFIRMED=Y</code>{' '}
        tags the close-of-bar verdict. earngush only auto-executes on Y so you don't get whipsawed by
        intra-bar repaints.
      </GuideCallout>

      <H id="alert-states">Alert states — what happens to each alert</H>
      <P>An alert moves through one of these statuses:</P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>RECEIVED</strong> — just arrived. Visible in Recent Alerts.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>PARKED</strong> — no broker credentials for this symbol's exchange, or auto-trade is off + you haven't acted yet. Stays in the Parked Alerts panel.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>ACKNOWLEDGED</strong> — you clicked Execute (or auto-trade did). Order is at the broker. The alert is now associated with a Trade row.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>DUPLICATE</strong> — same-symbol same-side alert arrived within the dedup window (default 60s). Suppressed silently.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>FAILED</strong> — order rejected by the broker (insufficient margin, IP not whitelisted, expired contract, etc.). The alert + the broker error are both in Logs.</>} />
        </ListItem>
      </List>

      <H id="position-lifecycle">Position lifecycle — from fill to close</H>
      <P>
        Once an alert is acknowledged and the broker confirms a fill, a <strong>Trade</strong> row is created.
        It progresses through these statuses:
      </P>
      <Paper variant="outlined" sx={{ p: 2, my: 1.5, overflowX: 'auto' }}>
        <Box component="pre" sx={{ m: 0, fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem', lineHeight: 1.5 }}>{`
  DRAFT ───▶ OPEN ───▶ CLOSED
    │          │
    │          ├──▶ SL active (real broker order at ISL)
    │          ├──▶ Target active (real broker order at target)
    │          ├──▶ TSL_ACTIVATED (price moved +TSL trigger %)
    │          ├──▶ TSL_TRAILED (TSL ratcheted up several times)
    │          └──▶ CLOSE_FIRED (one of: SL hit, Target hit, manual close, reverse signal)
    │
    └──▶ REJECTED (broker refused before fill)
`}</Box>
      </Paper>
      <P>
        The Trades page shows the full event log per trade. Click any row → expand drawer → vertical Stepper
        with one entry per state transition.
      </P>

      <H id="auto-vs-manual">Auto Trade vs manual confirmation</H>
      <P>
        The single most important toggle on the Dashboard. Default is <strong>OFF</strong> — start here unless
        you have full conviction in your signal source.
      </P>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>What happens on a new CONFIRMED=Y alert</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell><strong>Auto Trade OFF</strong></TableCell>
            <TableCell>Alert appears in Recent Alerts with an Execute button. No broker order is placed until you click.</TableCell>
          </TableRow>
          <TableRow>
            <TableCell><strong>Auto Trade ON</strong></TableCell>
            <TableCell>The order goes straight to the broker. Alert moves to ACKNOWLEDGED automatically.</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <P>
        Note that the global <strong>Order Placement kill switch</strong> overrides everything for new
        <em> entries</em> — if it's OFF, no new positions are opened regardless of auto-trade mode. Protective
        <em> exits</em> (stop-loss, target, trailing, manual / square-off closes) still go through. Use the
        kill switch when you want a hard pause on new risk (lunch break, market news, broker maintenance)
        without changing your strategy config.
      </P>

      <H id="risk-gate">The order gate — what blocks an order</H>
      <P>
        Every broker <em>entry</em> order — whether triggered manually or by auto-trade — passes through the
        same gate, which runs immediately before the broker call and can reject it. Protective exits bypass
        both checks below (reduce-only closes are never blocked). The two checks:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Kill switch</strong> — <code>orderPlacementEnabled</code>. If OFF, entry throws <code>OrderPlacementDisabledError</code> (exits unaffected).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Daily-loss cap</strong>: for each LIVE position, the loss it made since its exchange's trading day began (00:00 IST for Indian exchanges, 05:30 IST = 00:00 UTC for crypto), closed or still open. A position carried from yesterday counts only today's move; gains do not offset losses; paper trades are not counted. If <code>consumed ≥ cap</code>, entry throws <code>DailyLossCapBreachedError</code> (exits unaffected). If the app cannot read your settings (database error), new entries are blocked too, and exits still go through.</>} />
        </ListItem>
      </List>
      <P>
        Both checks are <strong>per-user</strong> (R80) — your cap doesn't affect another user, and your kill
        switch doesn't block them. Both checks fire on every order including SL / Target / TSL follow-ups, so
        if you blew through the cap mid-trade, future TSL trails on existing positions still execute (they're
        risk-reducing) — only new entry orders are blocked.
      </P>

      <H id="broker-model">Broker model — Delta vs Zerodha</H>
      <P>
        earngush abstracts both brokers behind the same <code>placeOrder()</code> interface but their
        underlying behaviours differ:
      </P>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Aspect</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Delta Exchange</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Zerodha</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow><TableCell>Asset class</TableCell><TableCell>Crypto perpetuals (USDT-margined)</TableCell><TableCell>NSE / BSE equities + NFO/CDS/MCX F&O</TableCell></TableRow>
          <TableRow><TableCell>Auth model</TableCell><TableCell>HMAC-signed REST with API key + secret. IP-allowlisted.</TableCell><TableCell>OAuth via Kite Connect — daily access-token refresh.</TableCell></TableRow>
          <TableRow><TableCell>Position side</TableCell><TableCell>Explicit long/short — both directions supported.</TableCell><TableCell>Equity: long only via MIS/CNC. F&O: both sides via NRML.</TableCell></TableRow>
          <TableRow><TableCell>SL/Target as broker orders</TableCell><TableCell>Native <code>stop_market_order</code> + <code>limit_order</code>.</TableCell><TableCell>SL-M for SL, LIMIT for target — both regular orders.</TableCell></TableRow>
          <TableRow><TableCell>Lot size</TableCell><TableCell>Fractional (e.g. BTCUSD lot = 0.001 BTC).</TableCell><TableCell>Integer; F&O has fixed lot sizes per contract.</TableCell></TableRow>
          <TableRow><TableCell>Margin</TableCell><TableCell>Cross / isolated, with explicit leverage knob (1× to 100×).</TableCell><TableCell>SPAN + Exposure for F&O; 5× intraday for equity MIS.</TableCell></TableRow>
        </TableBody>
      </Table>

      <H id="money-flow">Money flow — where the rupees go</H>
      <P>
        Two top-line numbers on your Dashboard tell the story:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Portfolio Value</strong> — cash + open-position mark-to-market across all connected brokers, in INR.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Available Margin</strong> — free cash usable for new orders. Equals Portfolio Value minus the margin currently locked in open positions.</>} />
        </ListItem>
      </List>
      <P>
        Per-trade: at entry, the broker locks the required margin (varies by leverage). As price moves,
        unrealised P&L floats — visible on each open Trade row. On close, unrealised becomes realised; cash
        increases / decreases accordingly. The daily-loss cap's enforcement gate counts losses booked today
        <em> plus</em> today's drawdown on open positions, so a single large open loser can trip the
        breaker intraday, before it's even booked. A position carried overnight counts only what it lost
        since the day began, not its whole loss since entry.
      </P>

      <GuideExample title="A trade through the lifecycle">
        09:35 — Alert arrives: BUY BTCUSD @ 80,000, SL 79,400 (₹600 risk per BTC, ~0.75%). Qty 0.01 BTC (₹800 margin at 10× leverage).<br />
        09:35 — Auto Trade is OFF; the alert sits in Recent Alerts. You click Execute.<br />
        09:35 — Order Gate passes (kill switch ON, today's loss ₹0 {'<'} cap ₹5,000). Order fires at Delta. Fill at 80,012.<br />
        09:35 — Trade row created status=OPEN. SL order planted at 79,400. Available Margin reduced by ₹800.<br />
        09:42 — Price reaches 80,332 (+0.4%, the TSL trigger). TSL_ACTIVATED logged. TSL trails up to 80,000 — your SL ratchets, now stop-loss above breakeven.<br />
        10:18 — Price reaches 80,520. TSL trails to 80,200.<br />
        10:25 — Price drops, hits TSL at 80,200. SL fires. Realised P&L = (80,200 − 80,012) × 0.01 = +1.88 USDT ≈ ₹157.
      </GuideExample>

      <H id="indicators-vs-strategies">Indicators vs strategies — the difference</H>
      <P>
        Both appear on the Charts page but they're different things:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Indicators</strong> are computed-and-drawn overlays — they don't generate signals. Example: an EMA(50) line, a Bollinger band, the C-POC line. You read them; the platform doesn't act on them.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Strategies</strong> are server-side scanners that watch candles and emit alerts. Example: Sniper buys a lower-timeframe pullback that prints a volume spike while the higher timeframe is trending up. The strategies use indicator math internally but the output is an actionable alert, not just a line on a chart.</>} />
        </ListItem>
      </List>
      <P>
        See <OpenInApp to="/app-guide#indicators" label="Indicators Reference" /> for every overlay, and
        <OpenInApp to="/app-guide#strategy-sniper" label="Sniper" />, <OpenInApp to="/app-guide#strategy-rockband" label="RockBand" />,
        <OpenInApp to="/app-guide#strategy-dailyblast" label="DailyBlast" /> for the strategies.
      </P>
    </>
  ),
  searchIndex: [
    { heading: 'Signal flow',              anchor: 'signal-flow',           body: 'signal flow tradingview webhook strategy in-house pipeline' },
    { heading: 'The Alert object',         anchor: 'alert-anatomy',         body: 'alert ticker action entry isl target strength confirmed' },
    { heading: 'Alert states',             anchor: 'alert-states',          body: 'alert state received parked acknowledged duplicate failed' },
    { heading: 'Position lifecycle',       anchor: 'position-lifecycle',    body: 'trade status draft open closed sl target tsl' },
    { heading: 'Auto Trade vs manual',     anchor: 'auto-vs-manual',        body: 'auto trade manual confirmation kill switch' },
    { heading: 'The order gate',           anchor: 'risk-gate',             body: 'order gate kill switch daily loss cap' },
    { heading: 'Broker model',             anchor: 'broker-model',          body: 'broker delta zerodha differences' },
    { heading: 'Money flow',               anchor: 'money-flow',            body: 'portfolio value available margin pnl realised unrealised' },
    { heading: 'Indicators vs strategies', anchor: 'indicators-vs-strategies', body: 'indicators strategies difference overlay scanner' },
  ],
};

export default Section_concepts;
